'use client';
import React, { useEffect, useRef, useState } from 'react';
import Constants from '../../../utils/constants';
import HttpService from '../../../services/HttpService';
import { useStore } from '../../../utils/store';
import { useNavigate, useParams } from 'react-router-dom';
import TextFieldComponent from '../../../components/form/TextFieldComponent';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import ButtonComponent from '../../../components/form/ButtonComponent';
import DeleteForeverIcon from '@mui/icons-material/Delete';
import SimpleDialogComponent from '../../../components/SimpleDialogComponent';
import SimpleAutoCompleteTextFieldComponent from '../../../components/form/SimpleAutoCompleteTextFieldComponent';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FormFieldData from '../../../Models/FormFieldData';
import AddButtonComponent from '../../../components/form/AddButtonComponent';
import CommonForm from '../../../components/form/CommonForm';
import AlertMessageComponent from '../../../components/AlertMessageComponent';
import { useAlert } from '../../../components/alertContext';
const ProjectForm: React.FC = () => {
  const isInitialLoad = useRef(true);
  const { getAdmin } = useStore();
  const { currentId } = useParams<{ currentId: any }>();
  const navigate = useNavigate();
  // State variables
  const { alert, setAlert, handleCloseAlert } = useAlert();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [currentBillable, setCurrentBillable] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any>(null);
  const [billableList, setBillableList] = useState<any[]>([]);
  const [billableFormItems, setBillableFormItems] = useState<any[]>([]);
  const [expandRowState, setExpandRowState] = useState<any[]>([]);
  const [billableRemovedItems, setBillableRemovedItems] = useState<any[]>([]);
  const [billableDetailsRemovedItems, setBillableDetailsRemovedItems] =
    useState<any[]>([]);
  const [projectForm, setProjectForm] = useState({
    name: '',
    notes: '',
    cost: '',
    unit: 'Each',
    id: null,
  });
  const [errors, setErrors] = useState({
    name: '',
    cost: '',
  });
  const handleArrow = async (index: number) => {
    setExpandRowState((prevItems) =>
      prevItems.includes(index)
        ? prevItems.filter((item) => item !== index)
        : [...prevItems, index]
    );
  };
  // Fetch single term record
  const fetchSingleRecord = async (id: string) => {
    try {
      const response = await HttpService.get(
        `${Constants.apiURL + Constants.projectsURL}/${id}`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        null
      );
      if (response.success) {
        handleRowClick(response.data);
      }
    } catch (error: any) {
      console.error(`GET Error:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };
  // Fetch data when component loads or dependencies change
  useEffect(() => {
    if (isInitialLoad.current) {
      if (currentId) {
        if (currentId !== 'Add') {
          fetchSingleRecord(currentId);
        }
      } else {
        setCurrentRecord(null);
      }

      if (billableList.length === 0) {
        getBillableList();
      }
      isInitialLoad.current = false;
    }
  }, [currentId]);

  const getBillableList = async () => {
    try {
      const response = await HttpService.get(
        Constants.apiURL + Constants.billableURL + '/getBillableWithResources',
        { headers: { Authorization: 'Bearer ' + getAdmin() } },
        {}
      );
      if (response.success) {
        const billableRecords = response.data;
        if (billableRecords.length > 0) {
          const updatedArr = billableRecords.map((item: any) => ({
            ...item,
            label: `${item.name} | ${item.unit}`,
            name: `${item.name}`,
          }));

          setBillableList(updatedArr);
          setCurrentBillable(updatedArr[0]);
        }
      }
      console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`PATCH Error:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };

  // Handle form input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProjectForm((prevData) => ({ ...prevData, [name]: value }));
    validateField(e.target);
  };

  const handleBillableChange = (value: any, index: number, type: string) => {
    setBillableFormItems((prevItems) => {
      // Create a copy of the current state
      const updatedItems = [...prevItems];
      // Update the specific item's property
      updatedItems[index][type] = value.toString();
      calculateTotalCost(updatedItems);
      return updatedItems;
    });
  };

  // TODO: Remove function?
  const handleBillableDetailChange = (
    value: any,
    index: number,
    type: string,
    detailIndex: number
  ) => {
    setBillableFormItems((prevItems) => {
      // Create a copy of the current state
      const updatedItems = [...prevItems];
      // Update the specific item's property
      updatedItems[index]['details'][detailIndex] = {
        ...updatedItems[index]['details'][detailIndex],
        [type]: value,
      };
      calculateTotalCost(updatedItems);
      return updatedItems;
    });
  };

  const AddBillableItem = () => {
    const index: any = billableList.findIndex(
      (item: any) => item.id.toString() === currentBillable.id.toString()
    );
    console.log(billableList[index]);
    if (index !== -1) {
      setBillableFormItems((prevItems) => {
        const newItems = [...prevItems];
        const indexData = billableFormItems.findIndex(
          (item: any) => item.id.toString() === currentBillable.id.toString()
        );
        if (indexData === -1) {
          newItems.push(billableList[index]);
        }
        calculateTotalCost(newItems);
        return newItems;
      });
    }
    setDialogOpen(false);
  };

  const removeItem = (index: any) => {
    setBillableFormItems((prevItems) => {
      const newItems = [...prevItems];
      newItems.splice(index, 1);
      calculateTotalCost(newItems);
      return newItems;
    });
    setBillableRemovedItems((prevItems) => {
      const newItems = [...prevItems];
      if (!newItems.includes(billableFormItems[index])) {
        newItems.push(billableFormItems[index]);
      }
      return newItems;
    });
  };

  const validateField = async (element: any) => {
    const elementName = element.name;
    const newErrors: any = {};
    switch (elementName) {
      case 'name':
        if (!element.value.trim()) {
          newErrors.name = 'Name is required.';
        }
        break;
      case 'cost':
        if (!element.value.trim()) {
          newErrors.cost = 'Price is required.';
        }
        break;
    }
    setErrors(newErrors);
    // Return true if no errors
    return Object.keys(newErrors).length === 0;
  };
  const validateForm = () => {
    const newErrors: any = {};

    // Name Validation
    if (!projectForm.name.trim()) {
      newErrors.name = 'Name is required.';
    }
    if (!projectForm.cost) {
      newErrors.cost = 'Price is required.';
    }
    setErrors(newErrors);
    // Return true if no errors
    return Object.keys(newErrors).length === 0;
  };
  const calculateTotalCost = (newItems: any) => {
    const totalCost = newItems.reduce((acc: number, item: any) => {
      const itemCost = item.details.reduce((detailAcc: number, detail: any) => {
        return detailAcc + parseInt(detail.quantity) * parseFloat(detail.cost);
      }, 0);
      return acc + itemCost;
    }, 0);
    setProjectForm((prev: any) => {
      return { ...prev, cost: isNaN(totalCost) ? 0.0 : totalCost };
    });
  };
  const handleBillableAdd = (e: any, dataValue: any) => {
    setCurrentBillable(dataValue);
  };
  // dialog Content for Billable List
  const dialogContent = () => {
    return (
      <div className="row mb-3 mt-2">
        <div style={{ width: '100%' }}>
          <SimpleAutoCompleteTextFieldComponent
            name="billableAutoSelect"
            value={currentBillable}
            isRequired={false}
            errorStr={''}
            keyName="billableAutoSelect"
            label="Billable Items"
            onChange={handleBillableAdd}
            items={billableList}
          />
        </div>
        <div className="col">
          <ButtonComponent
            keyName={'addBtn'}
            errors={errors}
            onClick={AddBillableItem}
            label={'Add'}
          />
        </div>
      </div>
    );
  };
  // Handle record creation or update
  const handleSave = async () => {
    if (validateForm()) {
      setLoading(true);
      const req: any = {
        name: projectForm.name,
        price: projectForm.cost,
        notes: projectForm.notes,
        unit: projectForm.unit,
        billableFormItems: billableFormItems,
      };
      const endpoint = `${Constants.apiURL + Constants.projectsURL}`;
      let response: any;
      try {
        if (currentRecord) {
          req['id'] = currentRecord.id;
          req['billableRemovedItems'] = billableRemovedItems;
          req['billableDetailsRemovedItems'] = billableDetailsRemovedItems;
          response = await HttpService.put(endpoint, req, {
            headers: { Authorization: `Bearer ${getAdmin()}` },
          });
        } else {
          response = await HttpService.post(endpoint, req, {
            headers: { Authorization: `Bearer ${getAdmin()}` },
          });
        }
        if (response.success) {
          clearForm();
        }
      } catch (error: any) {
        console.error(`PATCH Error:`, error);
        setAlert({
          type: 'error',
          message: error.message,
          open: true,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const getLineTotal = (itemData: any) => {
    let lineTotal = 0;
    if (itemData.details.length > 0) {
      lineTotal = 0;
      for (let i = 0; i < itemData.details.length; i++) {
        lineTotal =
          lineTotal +
          parseFloat(itemData.details[i].cost) *
            parseInt(itemData.details[i].quantity);
      }
      return lineTotal.toFixed(2);
    } else {
      return 0.0;
    }
  };
  // Clear form and reset state
  const clearForm = () => {
    setProjectForm({ name: '', id: null, cost: '', notes: '', unit: 'Each' });
    setCurrentRecord(null);
    setBillableFormItems([]);
    setBillableRemovedItems([]);
    setBillableDetailsRemovedItems([]);
    setAlert({
      type: 'error',
      message: '',
      open: false,
    });
    navigate('/data-management/project-template');
  };

  const handleRowClick = (record: any) => {
    setCurrentRecord(record.project);
    setProjectForm({
      name: record.project.name,
      id: record.project.id,
      cost: record.project.cost,
      notes: record.project.notes,
      unit: record.project.unit,
    });
    setBillableFormItems(record.billable);

    setErrors({
      name: '',
      cost: '',
    });
  };

  const billableDataElement = () => {
    return (
      <Box>
        <AddButtonComponent
          label=" Add Billable Description"
          keyName={'billableDesc'}
          onClick={() => {
            setDialogOpen(true);
          }}
        />

        {billableFormItems.length > 0 && (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              {billableFormItems.map((item: any, index: any) => {
                return (
                  <TableBody key={'tblbody_' + index}>
                    {index === 0 && (
                      <TableRow key={'headrow_' + index} className="headrowtbl">
                        <TableCell
                          key={'tblheadclldesc_' + index}
                          className="boldfont"
                        >
                          Billable Description
                        </TableCell>
                        <TableCell
                          key={'tblheadcllitemc_' + index}
                          className="boldfont"
                        >
                          Resource Items
                        </TableCell>
                        <TableCell
                          key={'tblbotblheadclliqty_' + index}
                          className="boldfont"
                          style={{ width: '70px' }}
                        >
                          Quantity
                        </TableCell>
                        <TableCell
                          key={'tblheadcllcost_' + index}
                          className="boldfont"
                          style={{ width: '150px' }}
                        >
                          Cost ($)
                        </TableCell>
                        <TableCell
                          key={'tblheadcluom_' + index}
                          className="boldfont"
                          style={{ width: '100px' }}
                        >
                          UOM
                        </TableCell>
                        <TableCell
                          key={'tblheadclttl_' + index}
                          className="boldfont"
                          style={{ width: '120px' }}
                          colSpan={2}
                        >
                          Total ($)
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow key={'bodyrow_' + index}>
                      <TableCell key={'bodyrow_cell_name' + index}>
                        {expandRowState.indexOf(index) !== -1 && (
                          <KeyboardArrowDownIcon
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              handleArrow(index);
                            }}
                          />
                        )}
                        {expandRowState.indexOf(index) === -1 && (
                          <ExpandLessIcon
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              handleArrow(index);
                            }}
                          />
                        )}
                        {item.name}
                      </TableCell>
                      <TableCell key={'bodyrow_cell_blnk' + index}></TableCell>
                      <TableCell key={'bodyrow_cell_qty' + index}>
                        <TextFieldComponent
                          label=""
                          keyName={'costFormBillable_' + index}
                          name={'costFormBillable_' + index}
                          value={item.quantity}
                          onChange={(event: any) => {
                            const newValue = event.target.value.replace(
                              /-/g,
                              ''
                            );
                            handleBillableChange(newValue, index, 'quantity');
                          }}
                          errorStr={''}
                          type="number"
                          isRequired={false}
                          style={{ width: '80px' }}
                        />
                      </TableCell>
                      <TableCell
                        key={'bodyrow_cell_lst_blnk' + index}
                      ></TableCell>
                      <TableCell key={'bodyrow_cell_lst_each' + index}>
                        Each
                      </TableCell>
                      <TableCell key={'bodyrow_cell_lst_linettl' + index}>
                        {getLineTotal(item)}
                      </TableCell>
                      <TableCell
                        key={'bodyrow_cell_lst_rmitem_main' + index}
                        style={{ width: '50px' }}
                      >
                        <IconButton
                          key={'bodyrow_cell_lst_rmitem_main_icn_btn' + index}
                          aria-label="delete"
                          onClick={() => {
                            removeItem(index);
                          }}
                        >
                          <DeleteForeverIcon
                            key={
                              'bodyrow_cell_lst_rmitem_main_icn_btn_icon' +
                              index
                            }
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {item['details'].map(
                      (detailItem: any, detailIndex: any) => {
                        return (
                          <TableRow
                            className={
                              expandRowState.indexOf(index) !== -1
                                ? 'hidden'
                                : ''
                            }
                            key={'bodyrow_dtl_' + index + '_' + detailIndex}
                          >
                            <TableCell
                              key={
                                'bodyrow_dtl_cell_blnk-' +
                                index +
                                '_' +
                                detailIndex
                              }
                            ></TableCell>
                            <TableCell
                              key={
                                'bodyrow_dtl_cell_name-' +
                                index +
                                '_' +
                                detailIndex
                              }
                            >
                              {detailItem.name}
                            </TableCell>
                            <TableCell
                              key={
                                'bodyrow_dtl_cell_qty-' +
                                index +
                                '_' +
                                detailIndex
                              }
                            >
                              {detailItem.quantity}
                              {/* <TextFieldComponent
                                label=""
                                keyName={'qtyFormBillable_' + index}
                                name={'qtyFormBillable_' + index}
                                value={detailItem.quantity}
                                onChange={(event) => {
                                  handleBillableDetailChange(
                                    event.target.value,
                                    index,
                                    'quantity',
                                    detailIndex
                                  );
                                }}
                                errorStr={''}
                                type="number"
                                isRequired={false}
                                style={{ width: '60px' }}
                              /> */}
                            </TableCell>
                            <TableCell
                              key={
                                'bodyrow_dtl_cell_cost-' +
                                index +
                                '_' +
                                detailIndex
                              }
                            >
                              {detailItem.cost}
                              {/* <TextFieldComponent
                                label=""
                                keyName={'costFormBillable_' + index}
                                name={'costFormBillable_' + index}
                                value={detailItem.cost}
                                onChange={(event: any) => {
                                  handleBillableDetailChange(
                                    event.target.value,
                                    index,
                                    'cost',
                                    detailIndex
                                  );
                                }}
                                errorStr={''}
                                type="number"
                                isRequired={false}
                                style={{ width: '80px' }}
                              /> */}
                            </TableCell>
                            <TableCell
                              key={
                                'bodyrow_dtl_cell_unit-' +
                                index +
                                '_' +
                                detailIndex
                              }
                            >
                              {detailItem.unit}
                            </TableCell>
                            <TableCell
                              key={
                                'bodyrow_dtl_cell_qty-ttl-' +
                                index +
                                '_' +
                                detailIndex
                              }
                            >
                              {parseFloat(detailItem.cost) *
                                parseInt(detailItem.quantity)}
                            </TableCell>
                            {/* <TableCell style={{ width: '50px' }}>
                              <IconButton
                                key={
                                  'bodyrow_dtl_cell_delet-icn-' +
                                  index +
                                  '_' +
                                  detailIndex
                                }
                                aria-label="delete"
                                onClick={() => {
                                  removeDetailsItem(index, detailIndex);
                                }}
                              >
                                <DeleteForeverIcon
                                  key={
                                    'bodyrow_dtl_cell_only-icn-' +
                                    index +
                                    '_' +
                                    detailIndex
                                  }
                                />
                              </IconButton>
                            </TableCell> */}
                          </TableRow>
                        );
                      }
                    )}
                  </TableBody>
                );
              })}

              {/* </TableBody> */}
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };
  const formFields: FormFieldData[] = [
    {
      name: 'name',
      label: 'Project Template  Name',
      value: projectForm.name,
      onChange: handleInputChange,
      isRequired: true,
      size: 12,
      type: 'textfield',
      validationRules: [
        (value) => (!value ? 'Project Template  Name required' : ''),
      ],
    },
    {
      isRequired: false,
      label: '',
      name: '',
      minRows: 7,
      value: '',
      onChange: () => {},
      size: 12,
      customComponent: billableDataElement(),
      type: 'custom',
    },
    {
      isRequired: false,
      label: 'Notes',
      name: 'notes',
      minRows: 7,
      value: projectForm.notes,
      onChange: handleInputChange,
      size: 12,
      type: 'textareaField',
    },
  ];
  return (
    <Stack>
      <CommonForm
        fields={formFields}
        onCancel={() => {}}
        formReset={clearForm}
        formTitle={
          currentId !== 'Add'
            ? 'Update Project Template'
            : 'Add New Project Template'
        }
        isLoading={loading}
        isUpdate={currentId !== 'Add' ? true : false}
        onSave={handleSave}
        actionButtonPosition="bottom"
      />

      {
        <SimpleDialogComponent
          title={'Billables'}
          dialogContent={dialogContent()}
          open={dialogOpen}
          width="70vw"
          handleClose={() => {
            setDialogOpen(false);
          }}
        />
      }
      <AlertMessageComponent
        type={alert.type}
        open={alert.open}
        handleClose={handleCloseAlert}
        message={alert.message}
      />
    </Stack>
  );
};

export default ProjectForm;
